import {
  Component, OnInit, Output, EventEmitter, OnDestroy, Input, OnChanges, SimpleChanges, AfterViewInit, Injector,
} from "@angular/core";
import { AbstractControl, FormControl, Validators } from "@angular/forms";
import { MatDialogConfig } from "@angular/material/dialog";
import { MatTableDataSource } from "@angular/material/table";
import { Subscription, Observable } from "rxjs";
import { ActionPendingCatalog, Catalog, PendingResourcesType, ResourceType, Roles } from "src/app/common/enumerations/enumerations";
import { RestrictionsStoreAndListenersService } from "src/app/components/scf-v2/store/restrictions/restrictions.store";
import { DisapprovalReasonComponent } from "src/app/controls/disapproval-reason/disapproval-reason.component";
import { YesNoDialogComponent } from "src/app/controls/yes-no-dialog/yes-no-dialog.component";
import { AcceleratorCatalog, AcceleratorCatalogDetails, AcceleratorHistory } from "src/app/components/catalogs/accelerator-catalog/services/accelerator-catalog";
import { BeamlineCatalog, Resource, ResourceRelation } from "src/app/components/catalogs/beamline-catalog/resource/resources";
import { Restriction } from "src/app/controls/restrictions/services/restriction";
import { PrivilegeEnum } from "src/app/common/enumerations/privilege-enum";
import { CatalogModel } from "../../catalog-model";
import { CatalogService } from "../../catalog-service";
import { Shielding } from "./accelerator-shielding/accelerator-shielding.component";
import { BaseComponent } from "src/app/common/base/base.component";
import { AcceleratorLocationComponent } from "../dialogs/location/location.component";
import { InfoDialogComponent } from "src/app/controls/info-dialog/info-dialog.component";
import { PendingAccelerator } from "src/app/services/pending-approvals/pending-approval";
import { Link } from "../../navigation-links/link/link";

@Component({
  selector: "app-accelerator-catalog-details",
  templateUrl: "./accelerator-catalog-details.component.html",
  styleUrls: ["./accelerator-catalog-details.component.scss"],
})
export class AcceleratorCatalogDetailsComponent extends BaseComponent implements OnInit, OnChanges, OnDestroy, AfterViewInit {
  @Input() acceleratorCatalog?: AcceleratorCatalog;
  @Input() showModification!: boolean;
  @Input() showHistory!: boolean;
  @Input() enableMove: boolean = false;

  @Output() saved = new EventEmitter<number>();
  @Output() changed = new EventEmitter<boolean>();
  @Output() showModificationChanged = new EventEmitter<boolean>();

  catalog = Catalog;

  catalogModel!: CatalogModel;
  accelerator!: Resource;

  restrictionsParent: any;
  locationsParent: any;

  restrictions!: Restriction[];
  restrictionsFiltered!: Restriction[];
  restrictions$!: Observable<Restriction[]>;
  restrictionsSubs!: Subscription;

  resources!: Resource[];
  resourcesFiltered!: Resource[];
  resources$!: Observable<Resource[]>;
  resourcesSubs!: Subscription;

  errorMsg!: string[];

  oldAcceleratorName?: string | null;

  canModify!: boolean;
  canApprove?: boolean | null;
  approvalInProgress = false;
  isEditing!: boolean;
  isCreating!: boolean;
  canCancel?: boolean | null;

  originalAccelerator?: AcceleratorCatalog | null;
  valid!: boolean;

  displayedColumnsLocations: string[] = ["location"];
  dataSourceLocation!: MatTableDataSource<Resource>;
  dataSourceRestriction!: MatTableDataSource<Restriction>;

  locations!: Resource[];
  shieldings: Shielding[] = [];

  formGroupSubs!: Subscription;
  isSharedChildenModified?: boolean;

  pendingSharedChangesRestrictionMessage?: string | null;

  public get nameCtrl(): AbstractControl | null {
    return this.formGroup.get("nameCtrl");
  }
  public get desciptionCtrl(): AbstractControl | null {
    return this.formGroup.get("descriptionCtrl");
  }
  public get showCtrl(): AbstractControl | null {
    return this.formGroup.get("showCtrl");
  }
  public get rfSystemCtrl(): AbstractControl | null {
    return this.formGroup.get("rfSystemCtrl");
  }
  public get activeCtrl(): AbstractControl | null {
    return this.formGroup.get("activeCtrl");
  }
  restrictionsCtrl = new FormControl();
  locationsCtrl = new FormControl();
  shieldingsCtrl = new FormControl();

  acceleratorHistory?: AcceleratorHistory[];

  constructor(
    protected override injector: Injector,
    restrictionsStore: RestrictionsStoreAndListenersService,
    private catalogService: CatalogService) {
    super(injector);
    restrictionsStore.initStore();
  }

  override  ngOnDestroy(): void {
    this.restrictionsSubs?.unsubscribe();
    this.resourcesSubs?.unsubscribe();
    super.ngOnDestroy();
  }

  async ngOnInit() {
    this.initializeForm();
    this.clear(true);
    this.loadResources();
    this.loadRestrictions();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['showModification'] && this.originalAccelerator && !this.showModification) {
      this.acceleratorCatalog = this.utils.cloneDeep(this.originalAccelerator);
    }
    if (changes["acceleratorCatalog"]) {
      this.isCreating = false;
      this.isEditing = false;
      this.showModification = false;
      this.showHistory = false;
      this.originalAccelerator = this.utils.cloneDeep(this.acceleratorCatalog);
    }
    this.initializeForm();
    this.getDetails(this.acceleratorCatalog);
    if (this.showHistory)
      this.getHistory();
  }

  checkForSharedChanges() {
    this.acceleratorCatalog?.details.shieldings.forEach(s => {
      if (this.acceleratorCatalog?.sharedChangesResourcesIDs?.includes(s.id)) {
        var changedParents = this.resources.filter(r => this.acceleratorCatalog?.sharedChangesParentsIDs?.includes(r.id));
        s.pendingChangeIn = changedParents.map(r => r.name).join(', ');
        s.hasPendingChanges = true;

        this.pendingSharedChangesRestrictionMessage =
          this.getMessage('PendingSharedChangesRestrictionMessage').description.replaceAll('{this}', this.acceleratorCatalog?.details.accelerator.name ?? '').replaceAll('{other}', changedParents.map(r => r.name).join(', '));
      }
    });


  }

  loadRestrictions() {
    this.restrictions$ = this.store.select((state) => state.Restrictions.data);
    this.restrictionsSubs = this.restrictions$.subscribe((data) => {
      if (data?.length) {
        this.restrictions = data;
      }
    });
  }

  loadResources() {
    this.resources$ = this.store.select((state) => state.Resources.data);
    this.resourcesSubs = this.resources$.subscribe((data) => {
      if (data.length) {
        this.resources = data;
      }
    });
  }

  ngAfterViewInit(): void { }

  initializeForm() {
    this.formGroup = this.formBuilder.group({
      nameCtrl: [null, [Validators.required, this.utils.IsWhiteSpaceReactiveForm]],
      descriptionCtrl: [
        null,
        [Validators.required, this.utils.IsWhiteSpaceReactiveForm],
      ],
      showCtrl: [null],
      rfSystemCtrl: [null],
      activeCtrl: [null],
      restrictionsCtrl: [null],
      shieldingsCtrl: [null],
    });
    this.disableControls(!this.isEditing);
    this.changed.emit(false);
    this.formGroupSubs = this.formGroup.valueChanges.subscribe((formValue) => {
      if (formValue) {
        this.changed.emit(this.formGroup.dirty);
      }
    });
    this.enableMove = false;
  }

  disableControls(disable: boolean = true) {
    if (disable) {
      this.formGroup.disable();
      this.nameCtrl?.disable();
      this.desciptionCtrl?.disable();
      this.showCtrl?.disable();
      this.rfSystemCtrl?.disable();
    } else {
      this.formGroup.enable();
      this.nameCtrl?.enable();
      this.desciptionCtrl?.enable();
      this.showCtrl?.enable();
      this.rfSystemCtrl?.enable();
    }
  }

  getDetails(acceleratorCatalog?: AcceleratorCatalog): void {
    this.formGroup?.markAsPristine();

    if (!acceleratorCatalog) {
      this.clear();
      return;
    }

    if (!acceleratorCatalog.id) {
      this.handleCreateMode(acceleratorCatalog);
      return;
    }

    this.handleEditMode(acceleratorCatalog);
  }

  private handleCreateMode(acceleratorCatalog: AcceleratorCatalog): void {

    this.canModify = this.hasPrivilege(PrivilegeEnum.AcceleratorCatalogCreate);
    this.isCreating = true;

    const source = acceleratorCatalog.details ?? acceleratorCatalog.pendingDetails;
    this.setValues(source);

    this.disableControls(false);
  }

  private handleEditMode(acceleratorCatalog: AcceleratorCatalog): void {
    const details = acceleratorCatalog.details;
    const pendingDetails = acceleratorCatalog.pendingDetails;
    const pendingAccelerator = acceleratorCatalog.pendingAcceleratorCatalog;

    this.accelerator = details?.accelerator ?? pendingDetails?.accelerator;

    this.canCancel =
      !!pendingAccelerator &&
      this.currentUser?.id === pendingAccelerator.createdBy;

    const shouldUsePending =
      !!pendingAccelerator &&
      (this.showModification || this.showHistory) &&
      !!this.acceleratorCatalog?.pendingDetails;

    const source = shouldUsePending
      ? this.acceleratorCatalog!.pendingDetails
      : details?.accelerator
        ? details
        : pendingDetails;

    this.checkForSharedChanges();

    this.isSharedChildenModified = !!this.acceleratorCatalog?.hasSharedChanges;

    this.setValues(source);

    const isOwner =
      !!pendingAccelerator &&
      pendingAccelerator.createdBy === this.currentUser?.id;

    this.canModify =
      this.hasPrivilege(PrivilegeEnum.AcceleratorCatalogCreate) &&
      (!pendingAccelerator || isOwner);

    this.canApprove =
      !!pendingAccelerator &&
      pendingAccelerator.createdBy !== this.currentUser?.id &&
      this.hasRoles([Roles.RP]);
  }

  /**
   * Saves pending catalog changes and keeps the resulting modification selected for review.
   */
  async save() {
    this.catalogModel.acceleratorCatalog = this.getValues();
    if (await this.isValid()) {
      console.log(this.accelerator.rfSystem);
      console.log(this.rfSystemCtrl?.value);
      this.isCreating = false;
      this.isEditing = false;
      this.catalogService
        .SaveAsDraft(this.catalogModel, Catalog.AcceleratorCatalog)
        .toPromise()
        .then(() => {
          this.setFormDirty(false);
          this.alert.info(
            this.isCreating
              ? "The New Accelerator Has Been Sent To Approval!"
              : "The Accelerator Modification Has Been Sent To Approval!"
          );
          this.saved.emit(this.accelerator.id);
          this.acceleratorCatalog = this.catalogModel.acceleratorCatalog;
          this.getDetails(this.acceleratorCatalog);

          // Keep the pending values visible after saving an existing accelerator modification.
          this.showModification = this.accelerator.id > 0;
          this.showModificationChanged.emit(this.showModification);
        }).catch(error => {
          console.log(error);
          this.alert.defaultError();
        });
    } else {
      this.alertError();
    }
    this.errorMsg = [];
  }

  async delete() {
    this.catalogModel.acceleratorCatalog = this.getValues();
    this.catalogModel.action = ActionPendingCatalog.Delete;
    this.showModificationChanged.emit(false);
    this.isCreating = false;
    this.isEditing = false;
    this.catalogService
      .SaveAsDraft(this.catalogModel, Catalog.AcceleratorCatalog)
      .toPromise()
      .then(() => {
        this.alert.info(
          "The Accelerator Deletion Has Been Sent To Approval!"
        );
        this.saved.emit(this.accelerator.id);
        this.clear();
      }).catch(error => {
        console.log(error);
        this.alert.defaultError();
      });
    this.errorMsg = [];
  }

  modifyAccelerator() {
    if (this.acceleratorCatalog?.pendingDetails)
      this.showModificationChanged.emit(true);
    this.isEditing = true;
    this.originalAccelerator = this.utils.cloneDeep(this.acceleratorCatalog);
    this.disableControls(false);
  }

  /**
   * Approves the pending accelerator catalog once and prevents duplicate submissions while the request is active.
   */
  approve() {
    if (this.approvalInProgress) return;

    this.approvalInProgress = true;
    this.showModificationChanged.emit(false);
    this.catalogService
      .Approve(
        this.accelerator.id ? this.accelerator.id : -(this.acceleratorCatalog?.id ?? 0),
        Catalog.AcceleratorCatalog
      )
      .toPromise().then(id => {
        this.alert.success(
          "Congratulations !! Accelerator Changes Approved Successfully!!"
        );
        this.saved.emit(id);
      }).catch((error) => {
        if (this.acceleratorCatalog?.pendingAcceleratorCatalog?.action == ActionPendingCatalog.Delete) {
          this.alert.error('Error Approving Deletion of Accelerator. The Accelerator may be in use in a Form or somewhere else.');
        }
        else if (error?.error) {
          this.alert.error(error.error);
        }
        else {
          this.alert.defaultError();
        }
      }).finally(() => {
        this.approvalInProgress = false;
      });
    this.errorMsg = [];
  }
  /**
   * Marks the current pending accelerator change as disapproved.
   *
   * @param disapprovalReason Reason entered by the approver.
   */
  disapprove(disapprovalReason: string) {
    // New catalog requests use the negative pending-request ID, matching the approval endpoint contract.
    const pendingRequestId = this.accelerator.id
      ? this.accelerator.id
      : -(this.acceleratorCatalog?.pendingAcceleratorCatalog?.id ?? this.acceleratorCatalog?.id ?? 0);
    this.catalogService
      .Unapprove(
        pendingRequestId,
        Catalog.AcceleratorCatalog,
        disapprovalReason
      )
      .subscribe(() => {
        this.alert.info(
          "Accelerator Change Disapproved!"
        );
        this.saved.emit(this.accelerator.id);
        this.clear();
      });
  }

  openAddLocationDialog(): void {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.data = new Resource();
    const dialogRef = this.dialog.open(AcceleratorLocationComponent, dialogConfig);
    dialogRef.afterClosed().subscribe((data) => {
      if (data) {
        if (
          this.isResourceRepeated(
            this.dataSourceLocation.data,
            data,
            "Location"
          )
        ) {
          this.addNewResource(data, this.dataSourceLocation);
          this.locationsParent = { list: this.dataSourceLocation.data };
          this.locationsCtrl.setValue(true);
          this.setFormDirty();
        } else {
          this.alertError();
        }
        this.errorMsg = [];
      }
    });
  }

  openEditLocationDialog(resource: Resource) {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.data = resource;
    const dialogRef = this.dialog.open(AcceleratorLocationComponent, dialogConfig);
    dialogRef.afterClosed().subscribe((data) => {
      if (data) {
        if (
          this.isResourceRepeated(
            this.dataSourceLocation.data,
            data,
            "Location"
          )
        ) {
          this.addExistingResource(data, resource, this.dataSourceLocation);
          this.locationsParent = { list: this.dataSourceLocation.data };
          this.locationsCtrl.setValue(true);
          this.setFormDirty();
        } else {
          this.alertError();
        }
        this.errorMsg = [];
      }
    });
  }

  openAddDisapprovalDialog() {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.data = "";
    const dialogRef = this.dialog.open(
      DisapprovalReasonComponent,
      dialogConfig
    );
    dialogRef.afterClosed().subscribe((data) => {
      if (data) {
        this.disapprove(data);
      }
    });
  }

  deleteLocation(resource: Resource) {
    this.dialogService.ConfirmationDialog('Remove', 'this <b>Location</b>').subscribe((result) => {
      if (result) {
        const index = this.dataSourceLocation.data.indexOf(resource);
        this.dataSourceLocation.data.splice(index, 1);
        this.dataSourceLocation.filter = "";
        this.locationsCtrl.setValue(true);
        this.setFormDirty();
      }
    });
  }

  /**
   * Normalizes the accelerator resource after its catalog name changes.
   */
  setAcceleratorName() {
    if (this.accelerator.name) {
      this.accelerator = this.createNewResource(this.accelerator, 1);
    }
    this.setFormDirty();
  }

  /**
   * Synchronizes the entered name immediately for restriction-location selection.
   */
  syncAcceleratorName() {
    // Keep the in-progress catalog named so the restriction dialog can include it before save.
    this.accelerator.name = this.nameCtrl?.value;
  }

  getDataFromRestriction(restrictions: Restriction[]) {
    this.restrictionsCtrl.setValue(restrictions);
    this.setFormDirty();
    this.dataSourceRestriction.data = restrictions;
  }

  // shieldingsChanged(shieldings: Shielding[]) {
  //   this.shieldingsCtrl.setValue(shieldings);
  //   this.setFormDirty();
  //   this.shieldings = shieldings;
  // }

  shieldingsChanged(shieldings: Shielding[]) {
    // Sync top-level windchillLinks down onto resource.windchillLinks
    shieldings.forEach(s => {
      if (s.resource) {
        s.resource.windchillLinks = s.windchillLinks ?? [];
      }
    });

    this.shieldings = shieldings;
    this.shieldingsCtrl.setValue(shieldings);

    // Push live state into pendingDetails so save + isModified both see it
    if (this.acceleratorCatalog?.pendingDetails) {
      this.acceleratorCatalog.pendingDetails.shieldings =
        shieldings
          .filter(s => !s.isDeleted)
          .map(s => s.resource as Resource);
    }

    this.setFormDirty();
  }

  addNewResource(
    data: Resource,
    dataSourceResource: MatTableDataSource<Resource>
  ) {
    const resource = this.createNewResource(
      data,
      ResourceType.AcceleratorLocation,
      dataSourceResource.data.length + 1
    );
    dataSourceResource.data.push(resource);
    dataSourceResource.filter = "";
  }

  addExistingResource(
    data: Resource,
    dataToUpdate: Resource,
    dataSourceResource: MatTableDataSource<Resource>
  ) {
    const index = dataSourceResource.data.indexOf(dataToUpdate);
    dataSourceResource.data[index].name = data.name;
    dataSourceResource.filter = "";
  }

  createNewResource(
    resource: Resource,
    type: number,
    position?: number
  ): Resource {
    const resourceCreated: Resource = {
      id: resource.id,
      name: resource.name,
      type,
      status: 1,
      position,
    };
    return resourceCreated;
  }

  getColumns() {
    return this.isEditing || this.isCreating ? ["location", "add"] : ["location"];
  }

  getValues(): AcceleratorCatalog | undefined {
    this.accelerator.name = this.nameCtrl?.value;
    this.accelerator.description = this.desciptionCtrl?.value;
    this.accelerator.checkboxVisible = this.showCtrl?.value;
    this.accelerator.rfSystem = this.rfSystemCtrl?.value;
    this.accelerator.status = this.activeCtrl?.value ? 1 : 0;
    if (this.dataSourceLocation.data.length > 0) {
      this.locations = this.dataSourceLocation.data;
    } else {
      this.locations = [];
    }
    if (this.dataSourceRestriction.data.length > 0) {
      this.restrictions = this.dataSourceRestriction.data;
    } else {
      this.restrictions = [];
    }
    if (this.acceleratorCatalog) {
      this.acceleratorCatalog.pendingDetails = new AcceleratorCatalogDetails();
      this.acceleratorCatalog.pendingDetails.shieldings = this.shieldings.filter(s => !s.isDeleted).map(
        (x) => {
          if (x.resource) {
            x.resource.checkboxVisible = x.checkboxVisible;
            x.resource.longShutdown = x.longShutdown;
            x.resource.inTunnel = x.inTunnel;
            x.resource.isRSSD = x.isRSSD;
            x.resource.position = x.position;
            x.resource.cssClass = x.resource.cssClass;
            x.resource.windchillLinks = x.windchillLinks;
            if (x.resource.parentResources) {
              const parentResource = x.resource.parentResources.find(
                (p) => p.parentResourceID == this.accelerator.id
              );
              if (parentResource) {
                parentResource.position = x.position;
              }
            }
            x.resource.childResources = x.childResources;
            x.resource.childResources?.map((x) => {
              x.childResource = null;
              x.parentResource = null;
            });
          }
          return x.resource;
        }

      ) as Resource[];
      this.acceleratorCatalog.pendingDetails.accelerator = this.accelerator;
      this.acceleratorCatalog.pendingDetails.restrictions = this.restrictions;
      this.acceleratorCatalog.pendingDetails.locations = this.locations;
      this.acceleratorCatalog.pendingAcceleratorCatalog = null;
      this.acceleratorCatalog.details.locations = [];
      this.acceleratorCatalog.details.restrictions = [];
      this.acceleratorCatalog.details.shieldings = [];

      this.checkChanges();

    }
    return this.acceleratorCatalog;
  }

  checkChanges() {
    let hasResourceChanges = false;
    let hasShieldingChanges = false;

    hasResourceChanges = this.checkModified('name');

    hasResourceChanges = hasResourceChanges || this.checkModified('description');
    hasResourceChanges = hasResourceChanges || this.checkModified('show');
    hasResourceChanges = hasResourceChanges || this.checkModified('rf');
    hasResourceChanges = hasResourceChanges || this.checkModified('active');
    hasResourceChanges = hasResourceChanges || this.checkModified('restrictions');
    hasResourceChanges = hasResourceChanges || this.checkModified('locations');

    hasShieldingChanges = this.checkModified('shieldings');
    if (this.acceleratorCatalog) {
      this.acceleratorCatalog.hasResourceChanges = hasResourceChanges;
      this.acceleratorCatalog.hasShieldingChanges = hasShieldingChanges;
    }
  }

  /**
   * Populates the catalog view and restores restriction locations omitted from existing catalog responses.
   *
   * @param data Catalog details to display.
   */
  setValues(data: AcceleratorCatalogDetails) {
    const fields = ["locations", "restrictions", "shieldings"];
    fields.map((f) => this.isModified(f));
    if (this.showHistory || this.showModification)
      this.checkAddedAndDeletedRows();
    this.accelerator = data.accelerator;
    this.accelerator?.childResources?.map((c) => {
      const resource = this.resources.find((r) => r.id == c.childResourceID);
      c.childResourceType = resource?.type;
      c.childResourceName = resource?.name;
      c.childResource = resource;
    });
    const locations = data.locations;
    this.dataSourceLocation = new MatTableDataSource(locations);
    // Existing catalog restrictions only include locationID, so resolve the resource for display and editing.
    const restrictions = this.resolveRestrictionLocations(data.restrictions, locations);
    this.dataSourceRestriction = new MatTableDataSource(restrictions);
    this.restrictionsParent = { list: restrictions };
    this.locationsParent = { list: locations };
    this.oldAcceleratorName = data.accelerator?.name;
    this.shieldings = this.getShieldings(this.utils.cloneDeep(data));
    this.nameCtrl?.setValue(this.accelerator?.name);
    this.desciptionCtrl?.setValue(this.accelerator?.description);
    this.showCtrl?.setValue(this.accelerator?.checkboxVisible);
    this.rfSystemCtrl?.setValue(this.accelerator?.rfSystem);
    this.activeCtrl?.setValue(this.accelerator?.status);
    this.restrictionsCtrl.setValue(false);
    this.locationsCtrl.setValue(false);
    this.shieldingsCtrl.setValue(false);
  }

  /**
   * Resolves restriction location objects from the catalog and shared resource lists.
   *
   * @param restrictions Restrictions returned with the catalog.
   * @param locations Accelerator locations owned by the catalog.
   * @returns Restrictions with their selected locations populated when a match exists.
   */
  private resolveRestrictionLocations(restrictions: Restriction[], locations: Resource[]): Restriction[] {
    const availableLocations = [this.accelerator, ...locations, ...(this.resources ?? [])].filter(resource => !!resource);
    restrictions.forEach(restriction => {
      if (!restriction.location && restriction.locationID) {
        restriction.location = availableLocations.find(resource => resource.id === restriction.locationID);
      }
      if (!restriction.location && restriction.locationName) {
        restriction.location = availableLocations.find(resource =>
          resource.name?.trim().toLowerCase() === restriction.locationName?.trim().toLowerCase()
        );
      }
    });
    return restrictions;
  }

  getShieldings(data: AcceleratorCatalogDetails) {
    return data.shieldings?.map((s) => {
      const parentResource = s.parentResources?.find(
        (p) => p.parentResourceID == this.accelerator.id
      );
      const childResources = this.getSubLocations(s);
      const shielding: Shielding = {
        name: s.name,
        resourceID: s.id,
        resource: s,
        position: s.isDeleted ? (s.position ?? 0) : (parentResource ? (parentResource.position ?? 0) : (s.position ?? 0)),
        childResources,
        checkboxVisible: s.checkboxVisible ?? false,
        longShutdown: s.longShutdown ?? false,
        inTunnel: s.inTunnel,
        isRSSD: s.isRSSD,
        isModified: s.isModified,
        isDeleted: s.isDeleted,
        isAdded: s.isAdded,
        isMovedDown: s.isMovedDown,
        isMovedUp: s.isMovedUp,
        isMovedDownAndModified: s.isMovedDownAndModified,
        isMovedUpAndModified: s.isMovedUpAndModified,
        subLocationNames: childResources
          ?.map((x) =>
            x.childResourceName
              ? x.childResourceName
              : this.resources.find((r) => r.id == x.childResourceID)?.name
          )
          .join(", "),
        hasPendingChanges: s.hasPendingChanges,
        pendingChangeIn: s.pendingChangeIn,
        windchillLinks: s.windchillLinks ?? [],
      };
      return shielding;
    }).sort((a, b) => this.utils.sort(a.position, b.position));
  }

  getSubLocations(s: Resource): ResourceRelation[] {
    let resourceRelations: ResourceRelation[] = [];
    let locationIDs = s.childResources?.map((c) => c.childResourceID);
    if (!locationIDs?.length) {
      const locs = s.parentResources?.filter((x) => x.locationsIDs && x.parentResourceID == this.accelerator.id).map((p) => p.locationsIDs).join(',');
      locationIDs = this.utils.JSONparse(locs) as number[];
    }
    if (locationIDs?.length) {
      const resources = this.resources.filter((r) => locationIDs?.includes(r.id));
      resources.map((r) => {
        const rr: ResourceRelation = {
          id: 0,
          childResourceID: r.id,
          childResource: r,
          parentResourceID: s.id,
          parentResource: s,
        };
        resourceRelations.push(rr);
      });
    }
    return resourceRelations;
  }

  /**
   * Discards local edits or cancels the current user's pending catalog request.
   * Pending creates are removed because no approved accelerator exists to restore.
   */
  cancel() {
    if (this.isEditing || this.isCreating) {
      this.formGroup.reset();
      if (this.isEditing && this.originalAccelerator) {
        this.acceleratorCatalog = this.originalAccelerator;
        this.getDetails(this.originalAccelerator);
        this.originalAccelerator = null;
      }
      if (this.isCreating) {
        this.canModify = false;
      }
      this.isCreating = false;
      this.isEditing = false;
      this.disableControls();
      this.setFormDirty(false);
    } else {
      const dialogConfig = new MatDialogConfig();
      dialogConfig.data = {
        message: "Are you sure you want to Cancel your changes?",
        icon: "question",
      };
      const dialogRef = this.dialog.open(YesNoDialogComponent, dialogConfig);
      dialogRef.afterClosed().subscribe((data) => {
        if (data) {
          // Delete pending creates instead of retaining a disapproved catalog entry.
          const cancelRequest = this.acceleratorCatalog?.pendingAcceleratorCatalog?.action == ActionPendingCatalog.Create
            ? this.catalogService.CancelPending(this.acceleratorCatalog.pendingAcceleratorCatalog.id, Catalog.AcceleratorCatalog, PendingResourcesType.Default)
            : this.catalogService.Unapprove(this.accelerator.id, Catalog.AcceleratorCatalog, '');

          cancelRequest.subscribe(() => {
            this.alert.info("Your changes were Cancelled");
            this.saved.emit(this.accelerator.id);
            this.clear();
          });
        }
      });
    }
  }

  isResourceRepeated(
    resources: Resource[],
    resource: Resource,
    typeName: string
  ) {
    this.valid = true;
    if (resources.filter((x) => x.name?.toLowerCase() === resource.name?.toLowerCase()
    ).length !== 0
    ) {
      this.errorMsg.push(
        typeName +
        " <b>" +
        resource.name?.toUpperCase() +
        "</b> is already added"
      );
      this.valid = false;
    }
    return this.valid;
  }

  async isValid() {
    this.valid = true;
    if (!this.nameCtrl?.value) {
      this.errorMsg.push("Field <b>Accelertor Name</b> should not be blank!");
      this.valid = false;
    }
    return this.valid;
  }

  clear(isInit?: boolean) {
    this.catalogModel = new CatalogModel();
    this.acceleratorCatalog = new AcceleratorCatalog();

    this.dataSourceLocation = new MatTableDataSource();
    this.dataSourceRestriction = new MatTableDataSource();

    this.shieldings = [];
    this.restrictionsParent = { list: [] };
    this.locationsParent = { list: [] };

    this.errorMsg = [];
    this.isCreating = false;
    this.isEditing = false;
    this.canApprove = false;
    this.canModify = false;
    this.canCancel = false;

    this.setFormDirty(false);
    this.formGroup.reset();
    this.disableControls();
  }

  alertError() {
    this.alert.error(this.errorMsg.join(", "));
    const that = this;
    setTimeout(() => {
      that.alert.clear();
    }, 3000);
  }

  private compareField(fieldName: string, baseDetails?: AcceleratorCatalogDetails, pendingDetails?: AcceleratorCatalogDetails): boolean {
    if (!pendingDetails?.accelerator || !baseDetails) return false;

    switch (fieldName) {
      case "name":
        return baseDetails.accelerator?.name !== pendingDetails.accelerator?.name;

      case "description":
        return baseDetails.accelerator?.description !== pendingDetails.accelerator?.description;

      case "show":
        return baseDetails.accelerator?.checkboxVisible !== pendingDetails.accelerator?.checkboxVisible;

      case "rfSystem":
        return baseDetails.accelerator?.rfSystem !== pendingDetails.accelerator?.rfSystem;

      case "active":
        return baseDetails.accelerator?.status !== pendingDetails.accelerator?.status;

      case "restrictions":
        const r1 = baseDetails.restrictions?.sort((a, b) => this.utils.sort(a.id, b.id)) ?? [];
        const r2 = pendingDetails.restrictions?.sort((a, b) => this.utils.sort(a.id, b.id)) ?? [];
        r2.forEach((r, i) => {
          const s = r1[i];
          if (!s) return;
          r.isModified =
            r.restrictionLanguage !== s.restrictionLanguage ||
            r.scf !== s.scf ||
            r.rsswa !== s.rsswa ||
            r.ebf !== s.ebf ||
            r.ppstb !== s.ppstb ||
            r.locationID !== s.locationID ||
            r.resourceID !== s.resourceID;
        });
        return r2.some(r => r.isModified) || r1.length !== r2.length;

      case "locations":
        const l1 = baseDetails.locations?.sort((a, b) => this.utils.sort(a.position, b.position)) ?? [];
        const l2 = pendingDetails.locations?.sort((a, b) => this.utils.sort(a.position, b.position)) ?? [];
        l2.forEach((l, i) => {
          const m = l1[i];
          l.isModified = !m || l.id !== m.id || l.name !== m.name;
        });
        return l2.some(l => l.isModified) || l1.length !== l2.length;

      case "shieldings":
        const s1 = this.getShieldings(baseDetails).sort((a, b) => this.utils.sort(a.position, b.position));
        const s2 = this.getShieldings(pendingDetails).sort((a, b) => this.utils.sort(a.position, b.position));
        s2.forEach(s => {
          if (!s.isDeleted) {
            const t = s1.find(x => x.resourceID === s.resourceID);
            const shielding = this.shieldings?.find(x => x.name === s.name);
            if (shielding) {
              // Use live shielding.windchillLinks (edited in child) vs base
              const currentLinks = shielding.windchillLinks ?? s.resource?.windchillLinks;
              const baseLinks = t?.windchillLinks ?? t?.resource?.windchillLinks;
              shielding.isModified =
                !t ||
                s.name !== t.name ||
                s.resource?.cssClass !== t.resource?.cssClass ||
                s.position !== t.position ||
                s.resource?.keText !== t.resource?.keText ||
                (s.longShutdown ?? false) !== (t.longShutdown ?? false) ||
                (s.inTunnel ?? false) !== (t.inTunnel ?? false) ||
                (s.isRSSD ?? false) !== (t.isRSSD ?? false) ||
                s.subLocationNames !== t.subLocationNames ||
                (s.checkboxVisible ?? false) !== (t.checkboxVisible ?? false) ||
                !this.areWindchillLinksEqual(currentLinks, baseLinks);
            }
          }
        });
        return this.shieldings.some(s => s.isModified) || s1.length !== s2.length;
    }

    return false;
  }

  private areWindchillLinksEqual(a: Link[] | null | undefined, b: Link[] | null | undefined): boolean {
    // Treat null/undefined/empty as equivalent
    const arrA = a ?? [];
    const arrB = b ?? [];
    if (arrA.length == 0 && arrB.length == 0) return true;
    if (arrA.length !== arrB.length) return false;

    return arrA.every((linkA, i) => {
      const linkB = arrB[i];
      return linkA?.id === linkB?.id
        && linkA?.hyperlink === linkB?.hyperlink
        && linkA?.webAddress === linkB?.webAddress;
    });
  }

  isModified(fieldName: string): boolean {
    if (this.acceleratorCatalog && (this.showModification || (this.showHistory && this.acceleratorHistory))) {
      return this.compareField(
        fieldName,
        this.acceleratorCatalog.details?.accelerator ? this.acceleratorCatalog.details : undefined,
        this.acceleratorCatalog.pendingDetails
      );
    }
    return false;
  }

  checkModified(fieldName: string): boolean {
    return this.compareField(
      fieldName,
      this.originalAccelerator?.details,
      this.acceleratorCatalog?.pendingDetails
    );
  }


  getHistory() {
    // this.acceleratorHistory = [];
    // this.catalogService.GetHistory(Catalog.AcceleratorCatalog, this.accelerator.id).toPromise().then(data => {
    //   if (data) {
    //     this.acceleratorHistory = data.acceleratorCatalogs;
    //     if (this.acceleratorHistory) {
    //       this.acceleratorHistory.map(h => {
    //         if (h.pendingAcceleratorCatalog) {
    //           h.pendingAcceleratorCatalog.userCreatedBy = this.users.find(u => u.id == h.pendingAcceleratorCatalog?.createdBy);
    //           h.pendingAcceleratorCatalog.statusByUser = this.users.find(u => u.id == h.pendingAcceleratorCatalog?.statusBy);
    //         }
    //       });
    //     }
    //   }
    // });
  }

  checkAddedAndDeletedRows() {
    if (this.showHistory || this.showModification) {
      // Restrictions
      const currentRestrictions = this.acceleratorCatalog?.pendingDetails?.restrictions ?? [];
      const prevRestrictions = this.acceleratorCatalog?.details?.restrictions ?? [];

      if (currentRestrictions && prevRestrictions) {
        prevRestrictions.map(p => {
          if (p.id && !currentRestrictions.find(c => c.id == p.id || c.restrictionLanguage == p.restrictionLanguage)) {
            p.isDeleted = true;
            currentRestrictions.push(p);
          }
        });
      }
      currentRestrictions?.filter(c => !c.isDeleted).map(c => {
        c.isAdded = c.id == 0 || !prevRestrictions?.find(p => p.id == c.id || c.restrictionLanguage == p.restrictionLanguage);
      });

      // Locations
      const currentLocations = this.acceleratorCatalog?.pendingDetails?.locations;
      const prevLocations = this.acceleratorCatalog?.details?.locations;

      if (currentLocations && prevLocations) {
        prevLocations.map(p => {
          if (p.id && !currentLocations.find(c => c.id == p.id)) {
            p.isDeleted = true;
            currentLocations.push(p);
          }
        });
      }
      currentLocations?.filter(c => !c.isDeleted).map(c => {
        c.isAdded = c.id == 0 || !prevLocations?.find(p => p.id == c.id);
      });

      // Shieldings
      const currentShieldings = this.acceleratorCatalog?.pendingDetails?.shieldings;
      const prevShieldings = this.acceleratorCatalog?.details?.shieldings;
      if (prevShieldings && currentShieldings) {
        prevShieldings.map(p => {
          const parentResource = p.parentResources?.find(
            r => r.parentResourceID == this.accelerator.id
          );
          if (p.id && !currentShieldings.find(c => c.id == p.id)) {
            p.isDeleted = true;
            p.position = parentResource ? (parentResource.position ?? 0) : (p.position ?? 0);
            currentShieldings.push(p);
          }
        });
      }
      // let position = 1;
      currentShieldings?.filter(c => !c.isDeleted).map(c => {
        // if (!c.position || c.position > position) {
        //   c.position = position;
        //   position++;
        // }
        // else position = c.position + 1;
        const p = prevShieldings?.find(p => p.id == c.id || p.name == c.name);
        c.isAdded = c.id == 0 || !p;
        const cParent = c.parentResources?.find(r => r.parentResourceID == this.accelerator.id);
        const pParent = p?.parentResources?.find(r => r.parentResourceID == this.accelerator.id);
        c.isModified = !c.isAdded && (
          c.name != p?.name ||
          c.keText != p?.keText ||
          (c.longShutdown ?? false) != (p?.longShutdown ?? false) ||
          (c.checkboxVisible ?? false) != (p?.checkboxVisible ?? false) ||
          (c.inTunnel ?? false) != (p?.inTunnel ?? false) ||
          c.subLocations != p?.subLocations ||
          (c.isRSSD ?? false) != (p?.isRSSD ?? false) ||
          this.areWindchillLinksEqual(c.windchillLinks, p?.windchillLinks)
        );
        c.isMovedUp = !c.isAdded && (c.position ?? 0) < (pParent?.position ?? p?.position ?? 0) && (pParent?.position ?? p?.position ?? 0) != 0;
        c.isMovedDown = !c.isAdded && (c.position ?? 0) > (pParent?.position ?? p?.position ?? 0) && (pParent?.position ?? p?.position ?? 0) != 0;
        if (c.isModified && c.isMovedUp) {
          c.isMovedUpAndModified = true;
          c.isMovedUp = false;
          c.isModified = false;
        }
        if (c.isModified && c.isMovedDown) {
          c.isMovedDownAndModified = true;
          c.isMovedDown = false;
          c.isModified = false;
        }
      });


    }
  }

  selectedHistory(acceleratorCatalog: AcceleratorCatalog) {
    this.acceleratorCatalog = acceleratorCatalog as AcceleratorCatalog;
    this.checkAddedAndDeletedRows();
    this.getDetails(this.acceleratorCatalog);
    if (acceleratorCatalog.noDataFound) {
      this.alert.warning("No Detailed Data found for this Modification!");
    }
  }

  infoPopup() {
    const dialog = this.dialog.open(InfoDialogComponent, {
      width: '45vw',
      data: {
        title: 'Accelerator Catalog Toggles',
        subTitle: '',
        message: '<b>Show?:</b> Determines whether this Accelerator Location will be shown or hidden on the Accelerator Status area of the Home Page. <br><br> <b>RF Related?:</b> When turned On, if this location is selected on an RSF form the RFEng group will be automatically notified when it becomes Active. <br><br> <b>Active:</b> When turned Off this accelerator location will be set to Inactive and will no longer appear as an option on dropdown menus',
        footer: ''
      }
    });
  }

}

